import {
  CommandCentrePosition,
  LinkHealth,
  OperatorPresence,
  OperatorPresenceUpdatedPayload,
  OperatorStatus,
  PositionUpdatedPayload,
  StateChangedPayload,
} from '@/types';

type PresenceLike = OperatorPresence | number | string | null | undefined;
type LinkHealthLike = LinkHealth | number | string | null | undefined;

const PRESENCE_BY_INDEX: OperatorPresence[] = ['Online', 'Away', 'Offline'];
const LINK_HEALTH_BY_INDEX: LinkHealth[] = ['Good', 'Degrading'];

export function normalizePresence(value: PresenceLike): OperatorPresence {
  if (value === 'Online' || value === 'Away' || value === 'Offline')
    return value;
  if (
    typeof value === 'number' &&
    value >= 0 &&
    value < PRESENCE_BY_INDEX.length
  ) {
    return PRESENCE_BY_INDEX[value];
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (
      !Number.isNaN(parsed) &&
      parsed >= 0 &&
      parsed < PRESENCE_BY_INDEX.length
    ) {
      return PRESENCE_BY_INDEX[parsed];
    }
  }
  return 'Offline';
}

export function normalizeLinkHealth(
  value: LinkHealthLike,
): LinkHealth | null | undefined {
  if (value == null) return value;
  if (value === 'Good' || value === 'Degrading') return value;
  if (
    typeof value === 'number' &&
    value >= 0 &&
    value < LINK_HEALTH_BY_INDEX.length
  ) {
    return LINK_HEALTH_BY_INDEX[value];
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (
      !Number.isNaN(parsed) &&
      parsed >= 0 &&
      parsed < LINK_HEALTH_BY_INDEX.length
    ) {
      return LINK_HEALTH_BY_INDEX[parsed];
    }
  }
  return undefined;
}

function readOptionalNumber(
  raw: Record<string, unknown>,
  camel: string,
  pascal: string,
): number | undefined {
  const value = raw[camel] ?? raw[pascal];
  if (value == null) return undefined;
  return Number(value);
}

function normalizeLegacyStatus(value: unknown): OperatorStatus {
  if (value === 'Live' || value === 'Online' || value === 'Away') return 'Live';
  if (value === 0 || value === '0') return 'Live';
  return 'Offline';
}

/** Accept camelCase or PascalCase SignalR payloads. */
export function coercePresencePayload(
  raw: Record<string, unknown>,
): OperatorPresenceUpdatedPayload {
  return {
    userId: Number(raw.userId ?? raw.UserId),
    locationId: Number(raw.locationId ?? raw.LocationId),
    latitude: readOptionalNumber(raw, 'latitude', 'Latitude'),
    longitude: readOptionalNumber(raw, 'longitude', 'Longitude'),
    status: normalizeLegacyStatus(raw.status ?? raw.Status),
    lastSeenAt: String(raw.lastSeenAt ?? raw.LastSeenAt ?? ''),
  };
}

/** Accept camelCase or PascalCase SignalR payloads. */
export function coercePositionPayload(
  raw: Record<string, unknown>,
): PositionUpdatedPayload {
  return {
    operatorId: Number(raw.operatorId ?? raw.OperatorId),
    siteId: Number(raw.siteId ?? raw.SiteId),
    lat:
      raw.lat != null || raw.Lat != null
        ? Number(raw.lat ?? raw.Lat)
        : undefined,
    lng:
      raw.lng != null || raw.Lng != null
        ? Number(raw.lng ?? raw.Lng)
        : undefined,
    accuracy:
      raw.accuracy != null || raw.Accuracy != null
        ? Number(raw.accuracy ?? raw.Accuracy)
        : undefined,
    presence: normalizePresence((raw.presence ?? raw.Presence) as PresenceLike),
    linkHealth: normalizeLinkHealth(
      (raw.linkHealth ?? raw.LinkHealth) as LinkHealthLike,
    ),
    ageSeconds: Number(raw.ageSeconds ?? raw.AgeSeconds ?? 0),
    secondsSinceLastMoved: readOptionalNumber(
      raw,
      'secondsSinceLastMoved',
      'SecondsSinceLastMoved',
    ),
  };
}

export function coerceStatePayload(
  raw: Record<string, unknown>,
): StateChangedPayload {
  return {
    operatorId: Number(raw.operatorId ?? raw.OperatorId),
    siteId: Number(raw.siteId ?? raw.SiteId),
    presence: normalizePresence((raw.presence ?? raw.Presence) as PresenceLike),
    linkHealth: normalizeLinkHealth(
      (raw.linkHealth ?? raw.LinkHealth) as LinkHealthLike,
    ),
    ageSeconds: Number(raw.ageSeconds ?? raw.AgeSeconds ?? 0),
    secondsSinceLastMoved: readOptionalNumber(
      raw,
      'secondsSinceLastMoved',
      'SecondsSinceLastMoved',
    ),
  };
}

function coordsChanged(
  previous: CommandCentrePosition | undefined,
  payload: PositionUpdatedPayload,
): boolean {
  if (!previous) return true;
  if (payload.lat == null || payload.lng == null) return true;
  return previous.lat !== payload.lat || previous.lng !== payload.lng;
}

/** Upsert — Offline + cleared coords means the operator left this site (remove). */
export function upsertPosition(
  list: CommandCentrePosition[],
  payload: PositionUpdatedPayload | StateChangedPayload,
): CommandCentrePosition[] {
  const index = list.findIndex((p) => p.operatorId === payload.operatorId);
  const previous = index === -1 ? undefined : list[index];

  if (
    payload.presence === 'Offline' &&
    'lat' in payload &&
    payload.lat == null &&
    'lng' in payload &&
    payload.lng == null
  ) {
    if (index === -1) return list;
    return list.filter((p) => p.operatorId !== payload.operatorId);
  }

  let secondsSinceLastMoved =
    payload.secondsSinceLastMoved ?? previous?.secondsSinceLastMoved ?? 0;
  if ('lat' in payload && coordsChanged(previous, payload)) {
    secondsSinceLastMoved = payload.secondsSinceLastMoved ?? 0;
  }

  const next: CommandCentrePosition = {
    operatorId: payload.operatorId,
    siteId: payload.siteId,
    lat: 'lat' in payload ? payload.lat : previous?.lat,
    lng: 'lng' in payload ? payload.lng : previous?.lng,
    accuracy: 'accuracy' in payload ? payload.accuracy : previous?.accuracy,
    presence: payload.presence,
    linkHealth: payload.linkHealth,
    ageSeconds: payload.ageSeconds,
    secondsSinceLastMoved,
  };

  if (index === -1) return [...list, next];
  const copy = [...list];
  copy[index] = { ...previous, ...next };
  return copy;
}

export function legacyStatusToPresence(
  status: OperatorStatus,
): CommandCentrePosition['presence'] {
  return status === 'Live' ? 'Online' : 'Offline';
}
